// Карта уровней — домашний экран играющего (UX-вайрфрейм, экран 03):
// топбар (профиль · мастерская · настройки), участок карты мира, нижний навбар.
//
// Карта постраничная и фуллскрин (решения автора 2026-07-06): свободного
// скролла нет, участок занимает всё пространство между топбаром и навбаром,
// поля — тёмный «остров» как на уровне; листание ‹ › играет слайд-«проезд».
// В DOM только 3 картинки (текущая + соседи-прелоады) — это прячет швы
// сгенерённых тайлов и экономит память.
// Участок = 1 тайл из web/src/meta/mapTiles.ts (10 узлов), мир = 3 участка.
// Кнопки уровней позиционируются по узлам тайла (координаты детерминированы
// генератором дорожки, см. docs/worldmap-tiles-spec.md §5).
// Контракт узлов (testid/data-атрибуты) — тот же, что был: их читают тесты.

import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/authStore';
import { LOCKED_ART } from '@/meta/lockedArt';
import { MAP_TILES, NODES_PER_TILE, TILES_PER_WORLD, type MapTile } from '@/meta/mapTiles';
import { BottomNav, MetaTopBar } from '@/meta/Shell';
import { usePortrait } from '@/meta/useOrientation';
import { useManifest, type Manifest, type ManifestLevel } from '@/meta/useManifest';
import { loadProgressMap, type ProgressMap } from '@/save/progress';
import { isCompleted, stepState, type NodeState } from '@/save/unlock';

function LevelNode({ level, state, stars, x, y }: {
  level: ManifestLevel; state: NodeState; stars: number; x: number; y: number;
}) {
  const style = { left: `${x * 100}%`, top: `${y * 100}%` };
  if (state === 'locked') {
    return (
      <span
        className="level-node map-node"
        style={style}
        data-testid={`level-${level.id}`}
        data-locked="true"
        aria-disabled="true"
        title="Сначала пройди предыдущий уровень"
      >
        <span className="node-face" aria-hidden="true">
          <img src={LOCKED_ART} alt="" />
        </span>
      </span>
    );
  }
  return (
    <Link
      to={`/play/${level.id}`}
      className="level-node map-node"
      style={style}
      data-testid={`level-${level.id}`}
      data-state={state}
    >
      <span className="node-face">{level.index}</span>
      {/* звёзды — жёлтые, дугой по верхней кромке круга (не вылезают вниз) */}
      <span
        className="node-stars"
        data-testid={`stars-${level.id}`}
        aria-label={`${stars} из 3 звёзд`}
      >
        {[0, 1, 2].map((i) => (
          <span key={i} className={`star s${i + 1}${i < stars ? ' on' : ''}`} aria-hidden="true">
            ★
          </span>
        ))}
      </span>
    </Link>
  );
}

/** Плейсхолдер неопубликованного уровня: манифест не отдаёт по нему ни id, ни
 *  данных (скачать нечего) — только позицию на карте. Визуально — тот же «?»,
 *  что у запертого уровня (решение автора: никакого своего визуала). Кликабелен
 *  ТОЛЬКО следующий за пройденным (state 'open') — клик открывает попап
 *  «Уровень дорабатывается»; остальные инертны, как locked. */
function WipNode({ worldId, index, state, x, y, onOpen }: {
  worldId: string; index: number; state: NodeState; x: number; y: number; onOpen: () => void;
}) {
  const style = { left: `${x * 100}%`, top: `${y * 100}%` };
  const face = (
    <span className="node-face" aria-hidden="true">
      <img src={LOCKED_ART} alt="" />
    </span>
  );
  if (state !== 'open') {
    return (
      <span
        className="level-node map-node"
        style={style}
        data-testid={`level-wip-${worldId}-${index}`}
        data-locked="true"
        data-wip="true"
        aria-disabled="true"
        title="Уровень дорабатывается"
      >
        {face}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="level-node map-node"
      style={style}
      data-testid={`level-wip-${worldId}-${index}`}
      data-wip="true"
      aria-label={`Уровень ${index} — в доработке`}
      title="Уровень дорабатывается"
      onClick={onOpen}
    >
      {face}
    </button>
  );
}

/** Слот-узел карты: позиция + (опубликованный уровень | плейсхолдер-черновик) + состояние. */
interface Slot {
  world: string; index: number; node: number; sec: number;
  level: ManifestLevel | null; state: NodeState;
}

/** Слоты ВСЕЙ карты с прогрессией: миры по index, в мире levelCount слотов
 *  (published-уровень или черновик). Шаг правила — stepState (канон unlock v1):
 *  пройденный — done (кликабелен, реиграй), следующий за пройденным — open
 *  (единственный кликабельный вперёд; для черновика клик = попап «дорабатывается»),
 *  всё остальное — locked «?». Черновик не может быть «пройден» — всё после него
 *  заперто до публикации. Фолбэк без levelCount — по максимальному published-индексу. */
function computeSlots(manifest: Manifest, progress: ProgressMap): Slot[] {
  const worlds = [...manifest.worlds].sort((a, b) => a.index - b.index);
  const maxCampaign = NODES_PER_TILE * TILES_PER_WORLD;
  const slots: Slot[] = [];
  let prevDone = true; // первый слот всегда открыт
  for (const w of worlds) {
    const inWorld = manifest.levels.filter((l) => l.world === w.id);
    const maxPublished = inWorld.reduce((m, l) => Math.max(m, l.index), 0);
    const count = Math.max(w.levelCount ?? 0, maxPublished);
    const byIndex = new Map(inWorld.map((l) => [l.index, l]));
    for (let i = 1; i <= count; i++) {
      const level = byIndex.get(i) ?? null;
      // слот-черновик (level === null) не бывает «пройден» — всё за ним заперто
      const done = level !== null && isCompleted(progress[level.id]);
      const state = stepState(prevDone, done);
      const part = Math.min(Math.floor((i - 1) / NODES_PER_TILE), TILES_PER_WORLD - 1);
      slots.push({
        world: w.id, index: i, sec: (w.index - 1) * TILES_PER_WORLD + part,
        // переполнение свыше 30 садится на последнюю площадку — защита от наложения узлов
        node: i > maxCampaign ? NODES_PER_TILE - 1 : (i - 1) % NODES_PER_TILE,
        level, state,
      });
      prevDone = done;
    }
  }
  return slots;
}

/** Позиция на карте: участок + его половина. Половина работает только в
 *  портрете (окно показывает пол-тайла), в альбоме всегда 0 — но хранится
 *  всё равно, чтобы поворот устройства не сбрасывал место игрока. */
interface Pos { sec: number; half: 0 | 1 }

/** Страница = участок (альбом) или половина участка (портрет). */
function pageOf(p: Pos, portrait: boolean): number {
  return portrait ? p.sec * 2 + p.half : p.sec;
}

function posOfPage(page: number, portrait: boolean): Pos {
  return portrait
    ? { sec: Math.floor(page / 2), half: (page % 2) as 0 | 1 }
    : { sec: page, half: 0 };
}

function pageCount(portrait: boolean): number {
  return portrait ? MAP_TILES.length * 2 : MAP_TILES.length;
}

/** Соседняя страница в направлении dir или null, если карта кончилась. */
function stepPos(p: Pos, dir: 1 | -1, portrait: boolean): Pos | null {
  const page = pageOf(p, portrait) + dir;
  if (page < 0 || page >= pageCount(portrait)) return null;
  const next = posOfPage(page, portrait);
  return portrait ? next : { sec: next.sec, half: p.half };
}

/** Слот, на котором игрок остановился: первый открытый, иначе последний
 *  пройденный. Он же — «дом» карты и цель кнопки «Играть». */
function currentSlot(slots: Slot[]): Slot | null {
  return slots.find((s) => s.state === 'open')
    ?? [...slots].reverse().find((s) => s.state === 'done')
    ?? null;
}

/** Стартовая позиция: участок текущего слота; без прогресса это уровень 1.
 *  Половину берём по x-координате его площадки на тайле — после рефреша
 *  игрок попадает на ту половину, где реально стоит его уровень. */
function initialPos(slots: Slot[]): Pos {
  const open = currentSlot(slots);
  if (!open) return { sec: 0, half: 0 };
  const x = MAP_TILES[open.sec]?.nodes[open.node]?.x ?? 0;
  return { sec: open.sec, half: x < 0.5 ? 0 : 1 };
}

function TileLayer({ slots, progress, sec, half, tile, className, onWip, onAnimationEnd }: {
  slots: Slot[];
  progress: ProgressMap | null;
  sec: number;
  half: 0 | 1;
  tile: MapTile;
  className: string;
  onWip: () => void;
  onAnimationEnd?: () => void;
}) {
  return (
    <div className={`map-layer ${className}`} onAnimationEnd={onAnimationEnd}>
      {/* .map-canvas — полный 2:1-кадр тайла: узлы позиционируются в его долях,
          вертикальный кроп низкого окна режет полотно вместе с узлами.
          В портрете полотно вдвое шире окна и сдвигается на --half: видна
          ровно одна половина участка (5 узлов), см. meta.css */}
      <div className="map-canvas" style={{ '--half': half } as CSSProperties}>
        {/* только статичный webp: идл-лупы убраны (решение автора 2026-07-30) —
            на iPhone под нагрузкой карта теряла декодированные картинки, вместе
            с артом участка гасли иконки интерфейса. Оживлять будем иначе */}
        <img src={tile.img} alt="" draggable={false} />
        {slots.filter((s) => s.sec === sec).map((s) => {
          const x = tile.nodes[s.node]?.x ?? 0.5;
          const y = tile.nodes[s.node]?.y ?? 0.5;
          return s.level
            ? (
              <LevelNode
                key={s.level.id}
                level={s.level}
                state={s.state}
                stars={progress?.[s.level.id]?.rating ?? 0}
                x={x}
                y={y}
              />
            )
            : (
              <WipNode
                key={`wip-${s.world}-${s.index}`}
                worldId={s.world}
                index={s.index}
                state={s.state}
                x={x}
                y={y}
                onOpen={onWip}
              />
            );
        })}
      </div>
    </div>
  );
}

/** Панель мира — портретный «низ» карты: куда игрок попал, сколько собрал и
 *  чем листать. В альбоме её нет: там эти же контролы плавают над сценой. */
function MapPanel({
  worldIndex, worldTitle, worldSlots, progress, dot, atHome, current,
  canPrev, canNext, onStep, onHome, onWip,
}: {
  worldIndex: number;
  worldTitle: string;
  worldSlots: Slot[];
  progress: ProgressMap | null;
  dot: number;
  atHome: boolean;
  current: Slot | null;
  canPrev: boolean;
  canNext: boolean;
  onStep: (dir: 1 | -1) => void;
  onHome: () => void;
  onWip: () => void;
}) {
  const done = worldSlots.filter((s) => s.state === 'done').length;
  const stars = worldSlots.reduce(
    (n, s) => n + (s.level ? progress?.[s.level.id]?.rating ?? 0 : 0),
    0,
  );
  const dots = TILES_PER_WORLD * 2; // мир = 3 участка = 6 половин

  return (
    <div className="map-panel" data-testid="map-panel">
      <p className="map-panel-world">
        Мир {worldIndex} · {worldTitle}
      </p>
      <p className="map-panel-stats">
        ★ {stars}/{worldSlots.length * 3} · уровни {done}/{worldSlots.length}
      </p>

      <div className="map-panel-pager">
        {canPrev
          ? (
            <button
              type="button"
              className="btn btn-round"
              data-testid="map-prev"
              aria-label="Предыдущий участок"
              onClick={() => onStep(-1)}
            >
              ‹
            </button>
          )
          : <span className="map-pager-gap" />}
        <span className="map-dots" aria-hidden="true">
          {Array.from({ length: dots }, (_, i) => (
            <i key={i} className={i === dot ? 'on' : undefined} />
          ))}
        </span>
        {canNext
          ? (
            <button
              type="button"
              className="btn btn-round"
              data-testid="map-next"
              aria-label="Следующий участок"
              onClick={() => onStep(1)}
            >
              ›
            </button>
          )
          : <span className="map-pager-gap" />}
      </div>

      {/* один слот действия: увело в сторону — вернуть карту, стоим дома —
          зайти в уровень (черновик вместо уровня открывает попап) */}
      {!atHome && (
        <button
          type="button"
          className="btn btn-lg btn-primary map-panel-cta"
          data-testid="map-home"
          onClick={onHome}
        >
          К текущему уровню
        </button>
      )}
      {atHome && current?.level && (
        <Link
          to={`/play/${current.level.id}`}
          className="btn btn-lg btn-primary map-panel-cta"
          data-testid="map-play"
        >
          Играть · уровень {current.index}
        </Link>
      )}
      {atHome && current && !current.level && (
        <button
          type="button"
          className="btn btn-lg btn-primary map-panel-cta"
          data-testid="map-play"
          onClick={onWip}
        >
          Играть · уровень {current.index}
        </button>
      )}
    </div>
  );
}

export function MapScreen() {
  const { manifest, error } = useManifest();
  const [progress, setProgress] = useState<ProgressMap | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  // слайд-«проезд»: пока не null — предыдущая страница уезжает, новая въезжает
  const [slide, setSlide] = useState<{ prev: Pos; dir: 1 | -1 } | null>(null);
  const [wipOpen, setWipOpen] = useState(false); // попап «уровень дорабатывается»
  const auth = useAuth();
  const portrait = usePortrait();

  useEffect(() => {
    if (auth.status === 'loading') return; // ждём bootstrap — иначе гейт посчитаем не по тому сейву
    let cancelled = false;
    void loadProgressMap().then((p) => { if (!cancelled) setProgress(p); });
    return () => { cancelled = true; };
  }, [auth.status]);

  const slots = manifest && progress ? computeSlots(manifest, progress) : null;

  // стартовая страница — там, где игрок остановился (первый открытый слот)
  useEffect(() => {
    if (pos !== null || !slots) return;
    setPos(initialPos(slots));
  }, [slots, pos]);

  const sec = Math.min(Math.max(pos?.sec ?? 0, 0), MAP_TILES.length - 1);
  const half = portrait ? pos?.half ?? 0 : 0;
  const here: Pos = { sec, half };
  const tile = MAP_TILES[sec];
  const world = manifest?.worlds.find((w) => w.index === tile.world) ?? null;
  const worldId = world?.id ?? `w${String(tile.world).padStart(2, '0')}`;

  const go = (next: Pos) => {
    if (next.sec === sec && next.half === half) return;
    setSlide({ prev: here, dir: pageOf(next, portrait) > pageOf(here, portrait) ? 1 : -1 });
    setPos(next);
  };
  const step = (dir: 1 | -1) => {
    const next = stepPos(here, dir, portrait);
    if (next) go(next);
  };

  // «дом» игрока: страница последнего доступного уровня. Она же — стартовая
  // при загрузке/обновлении страницы (см. initialPos).
  const home = slots ? initialPos(slots) : { sec: 0, half: 0 as const };
  const atHome = portrait ? home.sec === sec && home.half === half : home.sec === sec;

  // прелоад соседей: греем HTTP-кэш следующего клика.
  // Гранулярность — тайлы: половины одного участка делят картинку.
  // Раньше это были скрытые <img> прямо в дереве — но невидимая картинка всё
  // равно декодируется и висит битмапом (тайл 1376×688 ≈ 3.6 МБ, два соседа —
  // ещё 7.2 МБ поверх текущего). На iPhone под таким давлением Safari
  // выбрасывал уже декодированные спрайты, и карта с кнопками гасла
  // (правка автора 2026-07-30). Теперь ссылку на Image не держим: байты
  // остаются в кэше, а битмап сборщик волен освободить.
  const preloadSrc = [sec - 1, sec + 1]
    .filter((i) => i >= 0 && i < MAP_TILES.length)
    .map((i) => MAP_TILES[i].img)
    .join('|');
  useEffect(() => {
    if (!preloadSrc) return;
    for (const src of preloadSrc.split('|')) new Image().src = src;
  }, [preloadSrc]);

  // участок «скоро», только если в нём вообще нет узлов (за пределами levelCount мира)
  const hasSlots = slots ? slots.some((s) => s.sec === sec) : false;

  const canPrev = stepPos(here, -1, portrait) !== null;
  const canNext = stepPos(here, 1, portrait) !== null;

  return (
    <main
      className="meta-screen map-fullscreen"
      data-testid="screen-map"
      data-orient={portrait ? 'portrait' : 'landscape'}
    >
      {/* анизотропный гаусс (размытие только по X) — «моушен-блюр» проезда;
          url()-фильтры не интерполируются, сила меняется ступенями в кейфреймах */}
      <svg className="map-mblur-defs" aria-hidden="true" focusable="false">
        <defs>
          <filter id="map-mblur-strong" x="-25%" y="0%" width="150%" height="100%">
            <feGaussianBlur stdDeviation="18 0" />
          </filter>
          <filter id="map-mblur-soft" x="-15%" y="0%" width="130%" height="100%">
            <feGaussianBlur stdDeviation="7 0" />
          </filter>
        </defs>
      </svg>
      <MetaTopBar />
      <div className="map-stage" data-testid={`world-${worldId}`}>
        {error && (
          <output data-testid="map-error" className="error-box map-note">
            Не удалось загрузить карту: {error}
          </output>
        )}
        {!slots && !error && <p className="map-note">Загрузка…</p>}
        {slots && (
          <>
            {/* letterbox-окно статично, внутри едут только тайлы;
                поля вокруг — просто тёмный фон сцены */}
            <div className="map-wrap">
              <div className="map-viewport">
                {slide && (
                  <TileLayer
                    key={`out-${slide.prev.sec}-${slide.prev.half}`}
                    slots={slots}
                    progress={progress}
                    sec={slide.prev.sec}
                    half={slide.prev.half}
                    tile={MAP_TILES[slide.prev.sec]}
                    className={slide.dir === 1 ? 'map-slide-out-left' : 'map-slide-out-right'}
                    onWip={() => setWipOpen(true)}
                  />
                )}
                <TileLayer
                  key={`in-${sec}-${half}`}
                  slots={slots}
                  progress={progress}
                  sec={sec}
                  half={half}
                  tile={tile}
                  className={slide ? (slide.dir === 1 ? 'map-slide-in-right' : 'map-slide-in-left') : ''}
                  onWip={() => setWipOpen(true)}
                  onAnimationEnd={() => setSlide(null)}
                />
              </div>
              {/* плашка живёт внутри окна карты: в портрете под окном стоит
                  панель мира, и по нижней кромке сцены плашке места нет */}
              {!hasSlots && (
                <p className="map-soon" data-testid="map-soon">
                  Уровни этого участка ещё захардкожены — Дейв доберётся до них позже.
                </p>
              )}
            </div>
            {wipOpen && (
              <div
                className="map-overlay"
                data-testid="map-wip"
                role="dialog"
                aria-modal="true"
                aria-label="Уровень дорабатывается"
                onClick={() => setWipOpen(false)}
              >
                <div className="map-overlay-card" onClick={(e) => e.stopPropagation()}>
                  <p className="map-overlay-title">Уровень дорабатывается</p>
                  <p className="map-overlay-note">Дейв ещё собирает этот уровень — заглядывай позже.</p>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    data-testid="map-wip-close"
                    onClick={() => setWipOpen(false)}
                  >
                    Понятно
                  </button>
                </div>
              </div>
            )}
            {/* Портрет: под окном карты — панель мира, все контролы в ней.
                Альбом: контролы плавают над сценой, как раньше. Рендерится
                ровно один вариант, поэтому testid'ы не двоятся. */}
            {portrait && (
              <MapPanel
                worldIndex={tile.world}
                worldTitle={world?.title ?? '—'}
                worldSlots={slots.filter((s) => s.world === worldId)}
                progress={progress}
                dot={(sec - (tile.world - 1) * TILES_PER_WORLD) * 2 + half}
                atHome={atHome}
                current={currentSlot(slots)}
                canPrev={canPrev}
                canNext={canNext}
                onStep={step}
                onHome={() => go(home)}
                onWip={() => setWipOpen(true)}
              />
            )}
            {!portrait && !atHome && (
              <button
                type="button"
                className="btn btn-sm btn-primary map-home"
                data-testid="map-home"
                onClick={() => go(home)}
              >
                К текущему уровню
              </button>
            )}
            {/* недоступное направление листания — кнопки просто нет
                (правка автора 2026-07-07: задизейбленная стрелка не нужна) */}
            {!portrait && canPrev && (
              <button
                type="button"
                className="btn btn-round map-nav map-nav-prev"
                data-testid="map-prev"
                aria-label="Предыдущий участок"
                onClick={() => step(-1)}
              >
                ‹
              </button>
            )}
            {!portrait && canNext && (
              <button
                type="button"
                className="btn btn-round map-nav map-nav-next"
                data-testid="map-next"
                aria-label="Следующий участок"
                onClick={() => step(1)}
              >
                ›
              </button>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </main>
  );
}
